import ModalService from '@/modules/modals/services/modal.service';
import WCPushPromptModal from '@/modules/modals/components/WCPushPromptModal/WCPushPromptModal.vue';
import { isInStandaloneMode } from '@/utils/pwa-utils';
import pushNotificationCache, { CustomerRecord } from './pushNotificationCache.service';
import pushNotificationApi from './pushNotificationApi.service';
import webPermissions, { CLIENT_TYPE } from './webPermissions.service';
import firebasePlatformCommunication from './firebasePlatformCommunication.service';
import BrowserPermissionsError from '../../models/entities/push-notifications/browserPermissionsError';
import PushNotificationSupportParams from '../../models/entities/push-notifications/push-notification-support-params';

type PushNotificationSupportParamsInput = {
  enabledPlatforms: Array<string>;
  webPushConfig: string;
  publicVapidKey: string;
  isCustomer: boolean | string;
  isEmployee: boolean;
};

const resolvePushNotificationSupportParams = (
  enabledPlatformsOrParams: Array<string> | PushNotificationSupportParamsInput,
  webPushConfig: string = '',
  publicVapidKey: string = '',
  isCustomer: boolean | string = false,
  isEmployee: boolean = false,
): PushNotificationSupportParamsInput => {
  if (Array.isArray(enabledPlatformsOrParams)) {
    return {
      enabledPlatforms: enabledPlatformsOrParams,
      webPushConfig,
      publicVapidKey,
      isCustomer,
      isEmployee,
    };
  }

  return {
    enabledPlatforms: enabledPlatformsOrParams.enabledPlatforms || [],
    webPushConfig: enabledPlatformsOrParams.webPushConfig || '',
    publicVapidKey: enabledPlatformsOrParams.publicVapidKey || '',
    isCustomer: enabledPlatformsOrParams.isCustomer,
    isEmployee: enabledPlatformsOrParams.isEmployee,
  };
};

const persistPushNotificationDecisionInClient = async (
  customerKey: string,
  firebaseMessagingToken: string,
  lastRefreshDate: number,
  operationType: 'opt-in' | 'opt-out',
  overrideExistingRecord: boolean = false,
): Promise<void> => {
  try {
    const existingRecord: CustomerRecord | undefined = await pushNotificationCache.findRecord(
      customerKey,
    );

    console.debug(`Existing record before ${operationType}:`, existingRecord);

    const record: CustomerRecord = {
      CUS_ID: customerKey,
      FCM_TOKEN: firebaseMessagingToken,
      LAST_REFRESH_DATE: lastRefreshDate,
      CREATED_DATE: overrideExistingRecord
        ? Date.now()
        : existingRecord?.CREATED_DATE ?? Date.now(),
    };

    if (existingRecord) {
      await pushNotificationCache.updateRecord(record);
    } else {
      await pushNotificationCache.insertRecord(record);
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(
      `Failed to persist push notification ${operationType} decision: ${errorMessage}`,
    );
  }
};

const persistPushNotificationOptOutDecisionInClient = async (
  customerKey: string,
): Promise<void> => {
  await persistPushNotificationDecisionInClient(customerKey, '', 0, 'opt-out');
};

const persistPushNotificationOptInDecisionInClient = async (
  customerKey: string,
  firebaseMessagingToken: string,
  overrideExistingRecord: boolean = false,
): Promise<void> => {
  await persistPushNotificationDecisionInClient(
    customerKey,
    firebaseMessagingToken,
    Date.now(),
    'opt-in',
    overrideExistingRecord,
  );
};

const savePushNotificationTokenOnServer = async (firebaseMessagingToken: string): Promise<void> => {
  try {
    await pushNotificationApi.savePushNotificationTokenToDatabase(firebaseMessagingToken);
  } catch (error) {
    console.error('Failed to persist push notification token on server:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Failed to persist push notification token on server: ${errorMessage}`);
  }
};

const deletePushNotificationTokenFromServer = async (
  firebaseMessagingToken: string,
): Promise<void> => {
  try {
    await pushNotificationApi.deletePushNotificationTokenFromDatabase(firebaseMessagingToken);
  } catch (error) {
    console.error('Failed to delete push notification token from server:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Failed to delete push notification token from server: ${errorMessage}`);
  }
};

const isCustomerOptedInToPushNotifications = async (customerKey: string): Promise<boolean> => {
  try {
    const record: CustomerRecord | undefined = await pushNotificationCache.findRecord(customerKey);
    return record !== undefined && !!record.FCM_TOKEN;
  } catch (error) {
    console.error('Error checking if customer is opted in to push notifications:', error);
    return false;
  }
};

const hasCustomerMadePushNotificationDecision = async (customerKey: string): Promise<boolean> => {
  try {
    const record: CustomerRecord | undefined = await pushNotificationCache.findRecord(customerKey);
    return record !== undefined;
  } catch (error) {
    console.error('Error checking if customer has made push notification decision:', error);
    return false;
  }
};

const promptForBrowserWebPushNotificationPermission = async (): Promise<NotificationPermission> => {
  const permission: NotificationPermission = await webPermissions.promptForWebPushNotificationPermission();

  if (permission !== 'granted') {
    const errorMessage = `Push notification permission was ${permission}, expected 'granted'`;
    console.error(errorMessage);
    throw new BrowserPermissionsError(errorMessage, permission);
  }

  return permission;
};

const checkCurrentBrowserPermission = (): NotificationPermission | null => {
  const permission = webPermissions.getWebPushNotificationPermission();

  if (permission && permission !== 'granted') {
    const errorMessage = `Push notification permission is ${permission}, expected 'granted'`;
    console.error(errorMessage);
    throw new BrowserPermissionsError(errorMessage, permission);
  }

  return permission;
};

function isPushNotificationsSupported(params: PushNotificationSupportParams): boolean;
function isPushNotificationsSupported(
  enabledPlatforms: Array<string>,
  webConfig: string,
  vapidKey: string,
  isCustomer: boolean | string,
  isEmployee: boolean,
): boolean;
function isPushNotificationsSupported(
  enabledPlatformsOrParams: Array<string> | PushNotificationSupportParams,
  webConfig: string = '',
  vapidKey: string = '',
  isCustomer: boolean | string = false,
  isEmployee: boolean = false,
): boolean {
  const pushNotificationSupportParams = resolvePushNotificationSupportParams(
    enabledPlatformsOrParams,
    webConfig,
    vapidKey,
    isCustomer,
    isEmployee,
  );

  if (!pushNotificationSupportParams.isCustomer) {
    throw new Error('Push notifications are not enabled for guests');
  }

  if (pushNotificationSupportParams.isEmployee) {
    throw new Error('Push notifications are not enabled for employees');
  }

  if (
    !pushNotificationSupportParams.enabledPlatforms ||
    pushNotificationSupportParams.enabledPlatforms.length === 0
  ) {
    throw new Error('Push notifications are not enabled');
  }

  const clientType = webPermissions.getClientType();

  if (!pushNotificationSupportParams.enabledPlatforms.includes(clientType)) {
    throw new Error(
      `Push notifications are not supported for client type '${clientType}'. Enabled platforms: ${pushNotificationSupportParams.enabledPlatforms.join(
        ', ',
      )}`,
    );
  }

  if (!webPermissions.isIndexedDBSupported()) {
    throw new Error(
      'Push notifications are not supported: IndexedDB is not available in this browser/webview',
    );
  }

  if (clientType === CLIENT_TYPE.WEB) {
    if (!pushNotificationSupportParams.webPushConfig) {
      throw new Error('Push notifications are not configured: webPushConfig is missing');
    }

    if (!pushNotificationSupportParams.publicVapidKey) {
      throw new Error('Push notifications are not configured: publicVapidKey is missing');
    }

    if (!webPermissions.isWebPushSupported()) {
      throw new Error('Push notifications are not supported in this browser');
    }
  }

  console.debug('Push notifications are supported for this client');
  return true;
}

const attemptPushNotificationOptIn = async (
  customerKey: string,
  enabledPlatforms: Array<string>,
  webConfig: string,
  vapidKey: string,
  isEmployee: boolean,
): Promise<void> => {
  try {
    isPushNotificationsSupported(enabledPlatforms, webConfig, vapidKey, customerKey, isEmployee);

    const isOptedIn = await isCustomerOptedInToPushNotifications(customerKey);

    if (isOptedIn) {
      console.debug('Customer is already opted-in to push notifications, no action needed');
      return;
    }

    await promptForBrowserWebPushNotificationPermission();

    const firebaseMessagingToken = await firebasePlatformCommunication.retrieveFirebaseMessagingToken(
      webConfig,
      vapidKey,
    );

    try {
      await savePushNotificationTokenOnServer(firebaseMessagingToken);
    } catch (error) {
      console.error('Failed to save FCM token to server, unregistering from Firebase:', error);
      try {
        console.log('Rolling back by unregistering Firebase messaging token from Firebase');
        await firebasePlatformCommunication.deleteFirebaseMessagingToken(webConfig, vapidKey);
      } catch (deleteError) {
        console.error('Failed to delete Firebase messaging token during rollback:', deleteError);
        throw deleteError;
      }
      throw error;
    }

    await persistPushNotificationOptInDecisionInClient(customerKey, firebaseMessagingToken, true);

    console.debug('Successfully opted in to push notifications');
  } catch (error) {
    console.error('Failed to opt in to push notifications:', error);
    await persistPushNotificationOptOutDecisionInClient(customerKey);
    console.debug('Recording opt-out decision in client cache due to opt-in failure');
    throw error;
  }
};

const attemptPushNotificationOptOut = async (
  customerKey: string,
  webConfig: string,
  vapidKey: string,
): Promise<void> => {
  try {
    if (!customerKey) {
      throw new Error('Customer is not associated, cannot opt out of push notifications');
    }

    const record: CustomerRecord | undefined = await pushNotificationCache.findRecord(customerKey);

    if (!record?.FCM_TOKEN) {
      console.debug('Customer has not previously opted in, persisting opt-out decision only');
      await persistPushNotificationOptOutDecisionInClient(customerKey);
      return;
    }

    console.debug('Customer has previously opted in, proceeding with full opt-out process');

    const permission = checkCurrentBrowserPermission();
    console.debug('Current browser permission during opt-out:', permission);

    try {
      const numberOfRecordsWithSameToken = await pushNotificationCache.countRecordsByToken(record);

      if (numberOfRecordsWithSameToken > 1) {
        console.debug(
          'Multiple customers share the same FCM token, skipping Firebase unregistration',
        );
      } else {
        await firebasePlatformCommunication.deleteFirebaseMessagingToken(webConfig, vapidKey);
        console.debug('Successfully unregistered Firebase messaging token');
      }
    } catch (error) {
      console.error('Failed to unregister Firebase messaging token:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to unregister with Firebase: ${errorMessage}`);
    }

    try {
      const firebaseMessagingToken = record.FCM_TOKEN;
      await deletePushNotificationTokenFromServer(firebaseMessagingToken);
      console.debug('Successfully deleted push notification token from server');
    } catch (error) {
      console.error('Failed to delete push notification token from server (non-blocking):', error);
    }

    await persistPushNotificationOptOutDecisionInClient(customerKey);

    console.debug('Successfully opted out of push notifications');
  } catch (error) {
    console.error('Failed to opt out of push notifications:', error);
    throw error;
  }
};

const refreshPushNotificationToken = async (
  customerKey: string,
  webConfig: string,
  vapidKey: string,
  daysUntilRefresh: number,
  daysUtilServerExpiration: number,
): Promise<void> => {
  try {
    const record: CustomerRecord | undefined = await pushNotificationCache.findRecord(customerKey);

    if (!record?.FCM_TOKEN) {
      console.debug('Customer has not opted in to push notifications, skipping token refresh');
      return;
    }

    const currentTime = Date.now();
    const expirationThreshold = daysUtilServerExpiration * 24 * 60 * 60 * 1000;
    const tokenExpirationTime = record.CREATED_DATE + expirationThreshold;
    const isTokenExpired = currentTime >= tokenExpirationTime;
    const isStandalone = isInStandaloneMode();

    if (isTokenExpired) {
      console.debug(
        `Token has expired based on CREATED_DATE. Created: ${record.CREATED_DATE}, Expiration: ${tokenExpirationTime}, Current: ${currentTime}`,
      );
    } else if (isStandalone) {
      console.debug('App is running in standalone mode, refreshing token');
    } else {
      const timeSinceLastRefresh = currentTime - record.LAST_REFRESH_DATE;
      const refreshThreshold = daysUntilRefresh * 24 * 60 * 60 * 1000;

      if (timeSinceLastRefresh < refreshThreshold) {
        console.debug(
          `Token refresh not needed. Time since last refresh: ${timeSinceLastRefresh}ms, threshold: ${refreshThreshold}ms`,
        );
        return;
      }

      console.debug('Token refresh threshold exceeded, retrieving new Firebase messaging token');
    }

    const newFirebaseMessagingToken = await firebasePlatformCommunication.retrieveFirebaseMessagingToken(
      webConfig,
      vapidKey,
    );

    if (newFirebaseMessagingToken === record.FCM_TOKEN && !isTokenExpired) {
      console.debug('Firebase messaging token has not changed, updating refresh date only');
      await persistPushNotificationOptInDecisionInClient(customerKey, newFirebaseMessagingToken);
      return;
    }

    if (isTokenExpired) {
      console.debug(
        'Token likely expired on server, updating server and local cache with new token',
      );
    } else {
      console.debug('Firebase messaging token has changed, updating server and local cache');
    }

    try {
      console.debug('Deleting old push notification token from server - ', record.FCM_TOKEN);
      await deletePushNotificationTokenFromServer(record.FCM_TOKEN);
    } catch (error) {
      console.error('Failed to delete old push notification token from server:', error);
    }

    console.debug('Saving new push notification token to server - ', newFirebaseMessagingToken);
    await savePushNotificationTokenOnServer(newFirebaseMessagingToken);

    console.debug('Persisting new push notification token in client cache');
    await persistPushNotificationOptInDecisionInClient(
      customerKey,
      newFirebaseMessagingToken,
      isTokenExpired,
    );

    console.debug('Successfully refreshed push notification token');
  } catch (error) {
    console.error('Failed to refresh push notification token:', error);
  }
};

const handlePushConfigOnUserChange = async (
  customerKey: string,
  isEmployee: boolean,
  enabledPlatforms: Array<string>,
  webConfig: string,
  vapidKey: string,
  daysUntilRefresh: number,
  daysUtilServerExpiration: number,
  routeName: string,
): Promise<void> => {
  try {
    try {
      isPushNotificationsSupported(enabledPlatforms, webConfig, vapidKey, customerKey, isEmployee);
    } catch (error) {
      console.debug('Push notifications are not supported:', error);
      return;
    }

    const hasMadeDecision = await hasCustomerMadePushNotificationDecision(customerKey);

    if (hasMadeDecision) {
      const isOptedIn = await isCustomerOptedInToPushNotifications(customerKey);

      if (isOptedIn) {
        try {
          console.debug('Initializing Firebase for foreground notifications');
          await firebasePlatformCommunication.registerFirebaseServiceWorker();
          await firebasePlatformCommunication.initializeFirebaseApp(webConfig);
          console.debug('Firebase initialized successfully for foreground notifications');
          await refreshPushNotificationToken(
            customerKey,
            webConfig,
            vapidKey,
            daysUntilRefresh,
            daysUtilServerExpiration,
          );
        } catch (error) {
          console.error('Failed to initialize Firebase for foreground notifications:', error);
        }
      } else {
        console.debug('Customer has previously opted out of push notifications');
      }
    } else if (routeName !== 'Notifications') {
      console.debug('Customer has not made a decision, showing push notification prompt');
      await ModalService.open(WCPushPromptModal);
    }
  } catch (error) {
    console.error('Error in handlePushConfigOnSiteLoad:', error);
  }
};

const isPushNotificationsEnabledAndUserOptedIn = async (
  enabledPlatforms: Array<string>,
  webConfig: string,
  vapidKey: string,
  customerKey: string,
  isEmployee: boolean,
): Promise<boolean> => {
  try {
    const isSupported = isPushNotificationsSupported(
      enabledPlatforms,
      webConfig,
      vapidKey,
      customerKey,
      isEmployee,
    );

    if (!isSupported) {
      return false;
    }

    if (customerKey) {
      return await isCustomerOptedInToPushNotifications(customerKey);
    }

    return false;
  } catch (error) {
    console.debug('Push notifications are not enabled or user is not opted-in:', error);
    return false;
  }
};

export default {
  isPushNotificationsSupported,
  attemptPushNotificationOptIn,
  attemptPushNotificationOptOut,
  hasCustomerMadePushNotificationDecision,
  isCustomerOptedInToPushNotifications,
  handlePushConfigOnUserChange,
  isPushNotificationsEnabledAndUserOptedIn,
};
